import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TimelineAction, TimelineMarker } from "@/state/useOmniCineTimeline";
import { snapTime } from "@/lib/omnicine/snapTime";

export interface RulerProps {
  durationSec: number;
  playheadSec: number;
  viewportStartSec: number;
  viewportEndSec: number;
  viewportWidthPx: number;
  pxPerSec: number;
  fps: number;
  markers: TimelineMarker[];
  snap: {
    enabled: boolean;
    mode: "frame" | "beat" | "second" | "marker";
    fps: number;
    bpm?: number;
    beatDiv?: number;
  };
  dispatch: (action: TimelineAction) => void;
  onRequestPanByPx: (deltaPx: number) => void;
  onRequestZoomAtViewportX: (localViewportX: number, wheelDeltaY: number) => void;
  onRequestModifierZoomAtViewportX?: (localViewportX: number, wheelDeltaY: number) => void;
  onSetZoomModeEnabled?: (enabled: boolean) => void;
  zoomModeEnabled: boolean;
  onScrubVisualStateChange?: (state: {
    hoverViewportX: number | null;
    scrubViewportX: number | null;
  }) => void;
}

export const RULER_HEIGHT_PX = 56;
const EPS = 1e-6;
const CTRL_ZOOM_DELTA_MAX_PX = 42;

type TickSteps = {
  majorStepSec: number;
  minorStepSec: number;
  frameStepSec: number;
  frameTicksActive: boolean;
  showFrameLabels: boolean;
  frameBadgeLabel: string | null;
};

type WheelAccumulator = {
  rafId: number | null;
  pendingPanPx: number;
  pendingZoomDeltaY: number;
  latestViewportX: number;
  hasZoom: boolean;
};

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function pickFrameInterval(targetFrames: number): number {
  const options = [1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 24, 30, 40, 48, 60, 72, 96, 120];
  const safeTarget = Math.max(1, targetFrames);
  return options.find((value) => value >= safeTarget) ?? options[options.length - 1];
}

function computeTickSteps(
  pxPerSec: number,
  fps = 30,
  snapMode: "frame" | "beat" | "second" | "marker" = "second",
  snapEnabled = false,
): TickSteps {
  const safeFps = Math.max(1, Math.floor(fps || 30));
  const frameStepSec = 1 / safeFps;
  const pxPerFrame = pxPerSec * frameStepSec;
  const prefersFrameGrid = snapEnabled && snapMode === "frame";

  if (pxPerFrame >= (prefersFrameGrid ? 3.5 : 4.5)) {
    const majorFrames = pickFrameInterval(Math.ceil((prefersFrameGrid ? 64 : 80) / Math.max(pxPerFrame, 1e-6)));
    return {
      majorStepSec: majorFrames * frameStepSec,
      minorStepSec: frameStepSec,
      frameStepSec,
      frameTicksActive: true,
      showFrameLabels: pxPerFrame >= (prefersFrameGrid ? 7.5 : 9.5),
      frameBadgeLabel: `${safeFps} fps`,
    };
  }

  if (pxPerSec < 40) {
    return { majorStepSec: 10, minorStepSec: 2, frameStepSec, frameTicksActive: false, showFrameLabels: false, frameBadgeLabel: null };
  }
  if (pxPerSec < 80) {
    return { majorStepSec: 5, minorStepSec: 1, frameStepSec, frameTicksActive: false, showFrameLabels: false, frameBadgeLabel: null };
  }
  if (pxPerSec < 160) {
    return { majorStepSec: 1, minorStepSec: 0.25, frameStepSec, frameTicksActive: false, showFrameLabels: false, frameBadgeLabel: null };
  }
  if (pxPerSec < 320) {
    return { majorStepSec: 0.5, minorStepSec: 0.1, frameStepSec, frameTicksActive: false, showFrameLabels: false, frameBadgeLabel: null };
  }
  if (pxPerSec < 640) {
    return { majorStepSec: 0.25, minorStepSec: 0.05, frameStepSec, frameTicksActive: false, showFrameLabels: false, frameBadgeLabel: null };
  }
  return { majorStepSec: 0.1, minorStepSec: 0.02, frameStepSec, frameTicksActive: false, showFrameLabels: false, frameBadgeLabel: null };
}

function stepIndexStart(startSec: number, stepSec: number): number {
  if (stepSec <= 0) return 0;
  return Math.ceil((startSec - EPS) / stepSec);
}

function stepIndexEnd(endSec: number, stepSec: number): number {
  if (stepSec <= 0) return 0;
  return Math.floor((endSec + EPS) / stepSec);
}

function isAlignedToStep(value: number, step: number): boolean {
  if (step <= 0) return false;
  const ratio = value / step;
  return Math.abs(ratio - Math.round(ratio)) <= 1e-4;
}

function formatRulerLabel(sec: number, fps: number, withFrames: boolean): string {
  const safeSec = Math.max(0, sec);
  const safeFps = Math.max(1, Math.floor(fps || 30));

  const wholeSeconds = Math.floor(safeSec);
  const minutes = Math.floor(wholeSeconds / 60);
  const seconds = wholeSeconds % 60;

  if (!withFrames) {
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }

  const frameFloat = (safeSec - wholeSeconds) * safeFps;
  const frames = clamp(Math.floor(frameFloat + 1e-4), 0, safeFps - 1);

  return `${minutes}:${seconds.toString().padStart(2, "0")}:${frames.toString().padStart(2, "0")}`;
}

export default function Ruler({
  durationSec,
  playheadSec,
  viewportStartSec,
  viewportEndSec,
  viewportWidthPx,
  pxPerSec,
  fps,
  markers,
  snap,
  dispatch,
  onRequestPanByPx,
  onRequestZoomAtViewportX,
  onRequestModifierZoomAtViewportX,
  onSetZoomModeEnabled,
  zoomModeEnabled,
  onScrubVisualStateChange,
}: RulerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wheelRef = useRef<WheelAccumulator>({
    rafId: null,
    pendingPanPx: 0,
    pendingZoomDeltaY: 0,
    latestViewportX: 0,
    hasZoom: false,
  });
  const hoverViewportXRef = useRef<number | null>(null);
  const scrubViewportXRef = useRef<number | null>(null);
  const precisionZoomRef = useRef<{ lastViewportX: number } | null>(null);
  const [hoverViewportX, setHoverViewportX] = useState<number | null>(null);
  const [scrubViewportX, setScrubViewportX] = useState<number | null>(null);

  const visibleStartSec = useMemo(
    () => clamp(viewportStartSec, 0, Math.max(0, durationSec)),
    [viewportStartSec, durationSec],
  );

  const visibleEndSec = useMemo(
    () => clamp(viewportEndSec, visibleStartSec, Math.max(visibleStartSec, durationSec)),
    [viewportEndSec, visibleStartSec, durationSec],
  );

  const { majorStepSec, minorStepSec, frameStepSec, frameTicksActive, showFrameLabels, frameBadgeLabel } = useMemo(
    () => computeTickSteps(pxPerSec, fps, snap.mode, snap.enabled),
    [pxPerSec, fps, snap.mode, snap.enabled],
  );

  const timeToLocalX = useCallback(
    (timeSec: number) => (timeSec - visibleStartSec) * pxPerSec,
    [visibleStartSec, pxPerSec],
  );

  const clientXToTime = useCallback(
    (clientX: number, rectLeft: number) =>
      clamp(visibleStartSec + (clientX - rectLeft) / Math.max(1e-6, pxPerSec), 0, durationSec),
    [visibleStartSec, pxPerSec, durationSec],
  );

  const hoverTimeSec = useMemo(() => {
    const localX = scrubViewportX ?? hoverViewportX;
    if (localX == null) return null;
    return clamp(visibleStartSec + localX / Math.max(1e-6, pxPerSec), 0, durationSec);
  }, [durationSec, hoverViewportX, pxPerSec, scrubViewportX, visibleStartSec]);

  const hoverFrameIndex = useMemo(() => {
    if (hoverTimeSec == null) return null;
    return Math.max(0, Math.round(hoverTimeSec * Math.max(1, Math.floor(fps || 30))));
  }, [fps, hoverTimeSec]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const widthCssPx = Math.max(1, Math.floor(viewportWidthPx || 1));
    const heightCssPx = RULER_HEIGHT_PX;
    const dpr = window.devicePixelRatio || 1;

    canvas.width = widthCssPx * dpr;
    canvas.height = heightCssPx * dpr;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, widthCssPx, heightCssPx);

    // Background: full viewport width, always fixed
    ctx.fillStyle = "rgba(0,0,0,0.58)";
    ctx.fillRect(0, 0, widthCssPx, heightCssPx);

    // Top highlight / bottom edge
    ctx.fillStyle = "rgba(255,255,255,0.06)";
    ctx.fillRect(0, 0, widthCssPx, 1);
    ctx.fillStyle = "rgba(255,255,255,0.12)";
    ctx.fillRect(0, heightCssPx - 1, widthCssPx, 1);

    ctx.strokeStyle = "rgba(255,255,255,0.10)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, 24.5);
    ctx.lineTo(widthCssPx, 24.5);
    ctx.stroke();

    ctx.lineWidth = 1;
    ctx.strokeStyle = frameTicksActive ? "rgba(34,211,238,0.34)" : "rgba(34,211,238,0.22)";

    for (let tickIndex = stepIndexStart(visibleStartSec, minorStepSec); tickIndex <= stepIndexEnd(visibleEndSec, minorStepSec); tickIndex += 1) {
      const t = tickIndex * minorStepSec;
      if (isAlignedToStep(t, majorStepSec)) continue;
      const x = Math.round(timeToLocalX(t)) + 0.5;
      if (x < 0 || x > widthCssPx) continue;

      ctx.beginPath();
      ctx.moveTo(x, frameTicksActive ? 30 : 36);
      ctx.lineTo(x, heightCssPx);
      ctx.stroke();
    }

    // Major ticks + labels
    ctx.font = "600 12px system-ui";
    ctx.textBaseline = "top";
    ctx.fillStyle = "rgba(255,255,255,0.92)";
    ctx.strokeStyle = "rgba(255,255,255,0.88)";
    ctx.lineWidth = 1.1;

    for (let tickIndex = stepIndexStart(visibleStartSec, majorStepSec); tickIndex <= stepIndexEnd(visibleEndSec, majorStepSec); tickIndex += 1) {
      const t = tickIndex * majorStepSec;
      const x = Math.round(timeToLocalX(t)) + 0.5;
      if (x < 0 || x > widthCssPx) continue;

      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, heightCssPx);
      ctx.stroke();

      if (t <= durationSec + EPS) {
        ctx.fillText(formatRulerLabel(t, fps, showFrameLabels), x + 6, 6);
      }
    }

    // Markers
    ctx.strokeStyle = "#22d3ee";
    ctx.lineWidth = 2;

    for (const m of markers) {
      if ((m as any)?.visible === false) continue;
      const t = Number((m as any)?.timeSec ?? 0);
      if (!Number.isFinite(t)) continue;
      if (t < visibleStartSec || t > visibleEndSec) continue;

      const x = Math.round(timeToLocalX(t)) + 0.5;
      if (x < 0 || x > widthCssPx) continue;

      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, 44);
      ctx.stroke();
    }

    // Playhead
    if (playheadSec >= visibleStartSec - EPS && playheadSec <= visibleEndSec + EPS) {
      const playheadX = Math.round(timeToLocalX(playheadSec)) + 0.5;
      if (playheadX >= 0 && playheadX <= widthCssPx) {
        ctx.strokeStyle = "#c4b5fd";
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.moveTo(playheadX, 0);
        ctx.lineTo(playheadX, heightCssPx);
        ctx.stroke();
      }
    }

    const hoverX = scrubViewportX ?? hoverViewportX;
    if (hoverX != null && hoverTimeSec != null && hoverFrameIndex != null) {
      const clampedHoverX = clamp(hoverX, 0, widthCssPx);
      const hoverLineX = Math.round(clampedHoverX) + 0.5;

      ctx.strokeStyle = scrubViewportX != null ? "rgba(250,204,21,0.92)" : "rgba(255,255,255,0.45)";
      ctx.lineWidth = scrubViewportX != null ? 1.8 : 1;
      ctx.beginPath();
      ctx.moveTo(hoverLineX, 0);
      ctx.lineTo(hoverLineX, heightCssPx);
      ctx.stroke();

      const hoverLabel = `F${hoverFrameIndex} · ${formatRulerLabel(hoverTimeSec, fps, true)}`;
      ctx.font = "700 10px system-ui";
      const padX = 6;
      const padY = 4;
      const textW = ctx.measureText(hoverLabel).width;
      const badgeW = Math.ceil(textW + padX * 2);
      const badgeH = 16;
      const badgeX = clamp(clampedHoverX - badgeW / 2, 4, Math.max(4, widthCssPx - badgeW - 4));
      const badgeY = 28;

      ctx.fillStyle = scrubViewportX != null ? "rgba(250,204,21,0.20)" : "rgba(15,23,42,0.82)";
      ctx.fillRect(badgeX, badgeY, badgeW, badgeH);
      ctx.strokeStyle = scrubViewportX != null ? "rgba(250,204,21,0.78)" : "rgba(255,255,255,0.24)";
      ctx.lineWidth = 1;
      ctx.strokeRect(badgeX + 0.5, badgeY + 0.5, badgeW - 1, badgeH - 1);
      ctx.fillStyle = "rgba(255,255,255,0.96)";
      ctx.fillText(hoverLabel, badgeX + padX, badgeY + padY);
    }

    const showLockedBadge =
      !!((import.meta as any)?.env?.DEV) &&
      (window as any)?.__oc_debug?.showRulerLockedBadge !== false;
    const badgeText = frameTicksActive && frameBadgeLabel ? `FRAME GRID ${frameBadgeLabel.toUpperCase()}` : showLockedBadge ? "LOCKED VIEW" : null;
    if (badgeText) {
      ctx.font = "700 10px system-ui";
      const padX = 6;
      const padY = 4;
      const textW = ctx.measureText(badgeText).width;
      const badgeW = Math.ceil(textW + padX * 2);
      const badgeH = 16;
      const x = Math.max(0, widthCssPx - badgeW - 8);
      const y = heightCssPx - badgeH - 6;

      ctx.fillStyle = frameTicksActive ? "rgba(34,211,238,0.18)" : "rgba(34,211,238,0.14)";
      ctx.fillRect(x, y, badgeW, badgeH);
      ctx.strokeStyle = frameTicksActive ? "rgba(34,211,238,0.72)" : "rgba(34,211,238,0.55)";
      ctx.lineWidth = 1;
      ctx.strokeRect(x + 0.5, y + 0.5, badgeW - 1, badgeH - 1);

      ctx.fillStyle = "rgba(185,255,255,0.95)";
      ctx.textBaseline = "top";
      ctx.fillText(badgeText, x + padX, y + padY);
    }
  }, [
    durationSec,
    playheadSec,
    visibleStartSec,
    visibleEndSec,
    viewportWidthPx,
    pxPerSec,
    fps,
    markers,
    majorStepSec,
    minorStepSec,
    frameStepSec,
    frameTicksActive,
    showFrameLabels,
    frameBadgeLabel,
    hoverViewportX,
    scrubViewportX,
    hoverTimeSec,
    hoverFrameIndex,
    timeToLocalX,
  ]);

  const commitPlayheadAtEvent = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();

      let nextPlayheadSec = clientXToTime(e.clientX, rect.left);
      nextPlayheadSec = snapTime(nextPlayheadSec, {
        enabled: snap.enabled,
        mode: snap.mode,
        fps: snap.fps,
        bpm: snap.bpm,
        beatDiv: snap.beatDiv,
        markers,
      });

      dispatch({
        type: "timeline/setPlayhead",
        payload: { playheadSec: nextPlayheadSec },
        meta: { source: "user" },
      } as any);
    },
    [clientXToTime, snap, markers, dispatch],
  );

  const flushWheel = useCallback(() => {
    const acc = wheelRef.current;
    acc.rafId = null;

    const panPx = acc.pendingPanPx;
    const zoomDeltaY = acc.pendingZoomDeltaY;
    const viewportX = acc.latestViewportX;
    const hasZoom = acc.hasZoom;

    acc.pendingPanPx = 0;
    acc.pendingZoomDeltaY = 0;
    acc.hasZoom = false;

    if (Math.abs(panPx) > 0.01) {
      onRequestPanByPx(panPx);
    }

    if (hasZoom && Math.abs(zoomDeltaY) > 0.01) {
      onRequestZoomAtViewportX(viewportX, zoomDeltaY);
    }
  }, [onRequestPanByPx, onRequestZoomAtViewportX]);

  const scheduleWheelFlush = useCallback(() => {
    const acc = wheelRef.current;
    if (acc.rafId != null) return;
    acc.rafId = requestAnimationFrame(flushWheel);
  }, [flushWheel]);

  const onWheel = useCallback(
    (e: React.WheelEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const rect = e.currentTarget.getBoundingClientRect();
      const localViewportX = clamp(e.clientX - rect.left, 0, Math.max(0, rect.width));
      const deltaScale = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? Math.max(1, rect.width) : 1;
      const deltaXpx = e.deltaX * deltaScale;
      const deltaYpx = e.deltaY * deltaScale;

      const acc = wheelRef.current;
      acc.latestViewportX = localViewportX;
      const ctrlWheelLikeGesture = e.ctrlKey;

      if (zoomModeEnabled && !ctrlWheelLikeGesture) {
        // In ruler-only zoom mode, suppress wheel pan drift so the timeline UI stays locked.
        if (acc.rafId != null) {
          cancelAnimationFrame(acc.rafId);
          acc.rafId = null;
        }
        acc.pendingPanPx = 0;
        acc.pendingZoomDeltaY = 0;
        acc.hasZoom = false;
        return;
      }

      if (ctrlWheelLikeGesture && !zoomModeEnabled) {
        if (acc.rafId != null) {
          cancelAnimationFrame(acc.rafId);
          acc.rafId = null;
        }
        acc.pendingPanPx = 0;
        acc.pendingZoomDeltaY = 0;
        acc.hasZoom = false;
        return;
      }

      if (ctrlWheelLikeGesture && zoomModeEnabled) {
        if (acc.rafId != null) {
          cancelAnimationFrame(acc.rafId);
          acc.rafId = null;
        }

        acc.pendingPanPx = 0;
        acc.pendingZoomDeltaY = 0;
        acc.hasZoom = false;

        const clampedZoomDeltaY = clamp(deltaYpx, -CTRL_ZOOM_DELTA_MAX_PX, CTRL_ZOOM_DELTA_MAX_PX);
        if (Math.abs(clampedZoomDeltaY) > 0.01) {
          // Immediate ctrl-zoom avoids the "stuck" feel on high-frequency touchpad pinch streams.
          onRequestZoomAtViewportX(localViewportX, clampedZoomDeltaY);
        }
        return;
      } else {
        if (acc.hasZoom) {
          acc.pendingZoomDeltaY = 0;
          acc.hasZoom = false;
        }
        const dominantDelta = Math.abs(deltaXpx) > Math.abs(deltaYpx) ? deltaXpx : deltaYpx;
        acc.pendingPanPx += dominantDelta;
      }

      scheduleWheelFlush();
    },
    [scheduleWheelFlush, zoomModeEnabled],
  );

  useEffect(() => {
    return () => {
      const acc = wheelRef.current;
      if (acc.rafId != null) {
        cancelAnimationFrame(acc.rafId);
        acc.rafId = null;
      }

      if (precisionZoomRef.current) {
        precisionZoomRef.current = null;
      }
    };
  }, []);

  const stopPrecisionZoom = useCallback(() => {
    if (!precisionZoomRef.current) return;
    precisionZoomRef.current = null;
    onSetZoomModeEnabled?.(false);
  }, [onSetZoomModeEnabled]);

  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const rect = e.currentTarget.getBoundingClientRect();
      const localViewportX = clamp(e.clientX - rect.left, 0, Math.max(0, rect.width));
      hoverViewportXRef.current = localViewportX;
      setHoverViewportX(localViewportX);
      if (e.altKey) {
        scrubViewportXRef.current = null;
        setScrubViewportX(null);
      } else {
        scrubViewportXRef.current = localViewportX;
        setScrubViewportX(localViewportX);
      }
      onScrubVisualStateChange?.({
        hoverViewportX: hoverViewportXRef.current,
        scrubViewportX: scrubViewportXRef.current,
      });

      if (e.altKey && e.button === 0) {
        precisionZoomRef.current = { lastViewportX: localViewportX };
        onSetZoomModeEnabled?.(true);
        return;
      }

      commitPlayheadAtEvent(e);
    },
    [commitPlayheadAtEvent, onScrubVisualStateChange, onSetZoomModeEnabled],
  );

  const onMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const localViewportX = clamp(e.clientX - rect.left, 0, Math.max(0, rect.width));
      hoverViewportXRef.current = localViewportX;
      setHoverViewportX(localViewportX);

      const precision = precisionZoomRef.current;
      if (precision) {
        if (e.buttons !== 1 || !e.altKey) {
          stopPrecisionZoom();
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        const dx = localViewportX - precision.lastViewportX;
        precision.lastViewportX = localViewportX;

        scrubViewportXRef.current = null;
        setScrubViewportX(null);
        onScrubVisualStateChange?.({
          hoverViewportX: hoverViewportXRef.current,
          scrubViewportX: scrubViewportXRef.current,
        });

        if (Math.abs(dx) > 0.01) {
          const modifierZoomDeltaY = clamp(-dx * 6.8, -CTRL_ZOOM_DELTA_MAX_PX, CTRL_ZOOM_DELTA_MAX_PX);
          onRequestModifierZoomAtViewportX?.(localViewportX, modifierZoomDeltaY);
        }
        return;
      }

      if (e.buttons !== 1) {
        if (scrubViewportXRef.current !== null) {
          scrubViewportXRef.current = null;
          setScrubViewportX(null);
        }
        onScrubVisualStateChange?.({
          hoverViewportX: hoverViewportXRef.current,
          scrubViewportX: scrubViewportXRef.current,
        });
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      scrubViewportXRef.current = localViewportX;
      setScrubViewportX(localViewportX);
      onScrubVisualStateChange?.({
        hoverViewportX: hoverViewportXRef.current,
        scrubViewportX: scrubViewportXRef.current,
      });

      commitPlayheadAtEvent(e);
    },
    [commitPlayheadAtEvent, onRequestModifierZoomAtViewportX, onScrubVisualStateChange, stopPrecisionZoom],
  );

  const onMouseUp = useCallback(() => {
    stopPrecisionZoom();
    if (scrubViewportXRef.current === null) return;
    scrubViewportXRef.current = null;
    setScrubViewportX(null);
    onScrubVisualStateChange?.({
      hoverViewportX: hoverViewportXRef.current,
      scrubViewportX: scrubViewportXRef.current,
    });
  }, [onScrubVisualStateChange, stopPrecisionZoom]);

  const onMouseLeave = useCallback(() => {
    stopPrecisionZoom();
    hoverViewportXRef.current = null;
    scrubViewportXRef.current = null;
    setHoverViewportX(null);
    setScrubViewportX(null);
    onScrubVisualStateChange?.({
      hoverViewportX: hoverViewportXRef.current,
      scrubViewportX: scrubViewportXRef.current,
    });
  }, [onScrubVisualStateChange, stopPrecisionZoom]);

  return (
    <canvas
      ref={canvasRef}
      aria-label="OmniCine timeline ruler"
      className="block select-none cursor-ew-resize"
      style={{
        height: RULER_HEIGHT_PX,
        width: viewportWidthPx,
        touchAction: "none",
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
      onWheel={onWheel}
    />
  );
}